import { createElement, type HTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { cx, gapAttr, type GapStep } from './library-kit';

/**
 * The LAYOUT VOCABULARY (D548 DS-LIBRARY): how a screen lays its blocks out without a `style=` prop. Every option is a
 * token NAME — a space step, a track-size key, a column count — and maps to a class of `library.css`; none accepts a length
 * or a style object (`style` is not even in the prop types). Spacing between page blocks is `Stack`/`Page`, a toolbar or a
 * button row is `Row`, a KPI strip is `KpiGrid`, a list beside its detail is `MasterDetail`, a kanban is `Board`.
 */

type Tag = 'div' | 'section' | 'article' | 'aside' | 'header' | 'footer' | 'main' | 'nav' | 'form' | 'ul' | 'ol' | 'li' | 'fieldset';
/** DOM attributes a layout block may carry — everything but `style` and `className` (a screen must not bring its own). */
type BlockAttrs = Omit<HTMLAttributes<HTMLElement>, 'style' | 'className' | 'children'>;

interface BlockProps extends BlockAttrs {
  readonly as?: Tag;
  /** An extra theme class (an effect-layer entrance, a `.vello-row` list host) — never a screen-local style class. */
  readonly className?: string;
  readonly children?: ReactNode;
}

function block(tag: Tag, className: string, data: Readonly<Record<string, string | undefined>>, props: BlockProps): ReactElement {
  // `style` is not in the prop types; a loosely-typed caller that still passes one gets it dropped, not applied.
  const { as: _as, className: extra, children, style: _style, ...rest } = props as BlockProps & { style?: unknown };
  const attrs: Record<string, unknown> = { ...rest, className: cx(className, extra) };
  for (const [k, v] of Object.entries(data)) if (v !== undefined) attrs[`data-${k}`] = v;
  return createElement(tag, attrs, children);
}

export type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline';

// ---- Stack ---------------------------------------------------------------------------------------------------------------

export interface StackProps extends BlockProps {
  /** The space step between children (`--space-N`). Default 4 (16px). */
  readonly gap?: GapStep;
  readonly align?: Align;
  readonly justify?: 'center' | 'between';
}

/** A column of blocks with one gap: replaces `display:flex; flexDirection:column; gap` and the `marginBottom` rhythm. */
export function Stack({ gap, align, justify, as = 'div', ...props }: StackProps): ReactElement {
  return block(as, 'vello-stack', { gap: gapAttr(gap), align, justify }, props);
}

// ---- Row / Cluster -------------------------------------------------------------------------------------------------------

export interface RowProps extends BlockProps {
  /** The space step between items. Default 3 (12px). */
  readonly gap?: GapStep;
  /** Cross-axis alignment. Default `center`. */
  readonly align?: Align;
  readonly justify?: 'start' | 'center' | 'end' | 'between';
  /** Let the items wrap onto more lines (toolbars, button rows on a narrow screen). */
  readonly wrap?: boolean;
}

/** A horizontal run of items with one gap: replaces `display:flex; gap; alignItems; flexWrap; justifyContent`. Distinct from the list's `.vello-row` button. */
export function Row({ gap, align, justify, wrap = false, as = 'div', ...props }: RowProps): ReactElement {
  return block(as, 'vello-cluster', { gap: gapAttr(gap), align, justify, wrap: wrap ? 'true' : undefined }, props);
}

/** Alias of {@link Row} (the same component under the name the audit used). */
export const Cluster = Row;

export interface FilterBarProps extends Omit<RowProps, 'align' | 'wrap' | 'justify'> {
  /** Name the bar for assistive tech (rendered as `role="group"` + `aria-label`). */
  readonly label?: string;
}

/** Plain text inside a {@link FilterBar} ("12 unread", a limit): centred on the bar's 48px control line, not hung from its top. */
export function FilterBarNote({ children }: { readonly children?: ReactNode }): ReactElement {
  return createElement('div', { className: 'vello-filterbar__note' }, children);
}

/**
 * A FILTER BAR (D580; owner, 2026-10-13: "a control with a label must never sit lower than its neighbours — anywhere"): a
 * wrapping row of filters in which labelled `Field`s and bare controls (a `SegmentedControl`, a `DateRangeInput`, a `SearchInput`,
 * a `MenuButton`, a `Button`) share ONE control line. Labelled fields start at the top; every other child is pushed down by the
 * label block a `Field` draws, so its control lands on the fields' control line (see "FILTER BARS" in `theme.css`). Compose a
 * period bar, a list toolbar or a report filter with this instead of a bare `Row` + `align`; never place a label above only some
 * of the controls by hand.
 */
export function FilterBar({ gap = 4, label, children, ...props }: FilterBarProps): ReactElement {
  return block('div', 'vello-cluster', { gap: gapAttr(gap), align: 'start', wrap: 'true' }, {
    ...props,
    className: cx('vello-filterbar', props.className),
    ...(label !== undefined ? { role: 'group', 'aria-label': label } : {}),
    children,
  });
}

// ---- Page ----------------------------------------------------------------------------------------------------------------

/** A page's vertical flow: column, the A08 page gap (18px). Put the page header, KPI strip, panels and lists in it. */
export function Page({ gap, as = 'div', ...props }: Omit<StackProps, 'align' | 'justify'>): ReactElement {
  return block(as, 'vello-page', { gap: gapAttr(gap) }, props);
}

// ---- Grid / KpiGrid ------------------------------------------------------------------------------------------------------

export interface GridProps extends BlockProps {
  /** A fixed number of equal columns (collapses to one on a phone). Use `min` instead for a responsive auto-fit grid. */
  readonly columns?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Responsive auto-fit by track minimum: `xs` 160px · `sm` 200px · `md` 280px · `lg` 360px (the FormGrid scale). */
  readonly min?: 'xs' | 'sm' | 'md' | 'lg';
  /** Default 4 (16px). */
  readonly gap?: GapStep;
}

/** A grid by column count or by auto-fit track minimum: replaces the `gridTemplateColumns` recipes. */
export function Grid({ columns, min, gap, as = 'div', ...props }: GridProps): ReactElement {
  return block(as, 'vello-grid', { cols: columns === undefined || min !== undefined ? undefined : String(columns), min, gap: gapAttr(gap) }, props);
}

/** The KPI row: `repeat(auto-fit, minmax(160px, 1fr))`, gap 12 (the A08 record page). Holds `StatCard`s. */
export function KpiGrid({ gap, as = 'div', ...props }: Omit<GridProps, 'columns' | 'min'>): ReactElement {
  return block(as, 'vello-kpi-grid', { gap: gapAttr(gap) }, props);
}

// ---- Split / MasterDetail ------------------------------------------------------------------------------------------------

export interface SplitProps extends BlockProps {
  /** A fixed-width first column beside a flexible one: `sm` 240px · `md` 300px (the A08 record body) · `lg` 360px. */
  readonly aside?: 'sm' | 'md' | 'lg';
  /** With `aside`: put the fixed column last. */
  readonly side?: 'start' | 'end';
  /** Two flexible columns by ratio (ignored when `aside` is set). Default equal. */
  readonly ratio?: '3-2' | '2-1' | '1-2' | '3-1';
  /** Stretch both panes to the taller one (default: top-aligned). */
  readonly stretch?: boolean;
  /** Default 4 (16px). */
  readonly gap?: GapStep;
}

/** Two columns (children = the two panes); stacks to one column where the shell's sidebar becomes a drawer (≤ 900px). */
export function Split({ aside, side, ratio, stretch = false, gap, as = 'div', ...props }: SplitProps): ReactElement {
  return block(as, 'vello-split', { aside, side: aside !== undefined && side === 'end' ? 'end' : undefined, ratio: aside === undefined ? ratio : undefined, align: stretch ? 'stretch' : undefined, gap: gapAttr(gap) }, props);
}

export interface MasterDetailProps extends Omit<SplitProps, 'children' | 'ratio' | 'side'> {
  readonly master: ReactNode;
  readonly detail: ReactNode;
  /** Width of the master column. Default `md` (300px). */
  readonly aside?: 'sm' | 'md' | 'lg';
}

/** A list (master) on the left, the selected record (detail) on the right: `300px 1fr`, gap 16. */
export function MasterDetail({ master, detail, aside = 'md', ...rest }: MasterDetailProps): ReactElement {
  return (
    <Split aside={aside} {...rest}>
      <div className="vello-split__pane">{master}</div>
      <div className="vello-split__pane">{detail}</div>
    </Split>
  );
}

// ---- DetailGrid ----------------------------------------------------------------------------------------------------------

export interface DetailGridProps extends BlockProps {
  /** The narrowest a card gets before the grid drops a column: `md` ~280px, `lg` ~352px (default), `xl` ~440px. */
  readonly min?: 'md' | 'lg' | 'xl';
  /** Default 4 (16px). */
  readonly gap?: GapStep;
  /**
   * `grid` (default): equal rows, cards top-aligned. `masonry` (D619): the cards stack down balanced columns with no hole under a
   * short card — for a record's many secondary panels of very different heights (reading order runs down each column).
   */
  readonly flow?: 'grid' | 'masonry';
}

/**
 * The data panels of a record page (a person, a driver, a merchant, a store) as a responsive grid of cards: as many equal
 * columns as the width allows (three on a wide desktop, two on a laptop, one on a phone), each at least `min` wide. Put
 * panels in it (wrap one that needs the whole row in {@link DetailCell}`wide`); the history or audit goes BELOW the grid at
 * full width, not in a narrow rail (owner, 2026-10-13: the data is more important than the history).
 */
export function DetailGrid({ min, gap, flow = 'grid', as = 'div', ...props }: DetailGridProps): ReactElement {
  return block(as, 'vello-detailgrid', { min: min === 'lg' ? undefined : min, gap: gapAttr(gap), flow: flow === 'masonry' ? 'masonry' : undefined }, props);
}

/** One cell of a {@link DetailGrid}; `wide` makes it span every column. */
export function DetailCell({ wide = false, as = 'div', ...props }: Omit<BlockProps, 'className'> & { readonly wide?: boolean }): ReactElement {
  return block(as, 'vello-detailgrid__cell', { wide: wide ? 'true' : undefined }, props);
}

// ---- Board ---------------------------------------------------------------------------------------------------------------

export interface BoardProps extends BlockProps {
  /** Pin the number of columns (2–5); omit to let every child column take its own 240px track and scroll sideways. */
  readonly columns?: 2 | 3 | 4 | 5;
  /** Default 3 (12px). */
  readonly gap?: GapStep;
}

/** Kanban columns side by side (holds `BoardColumn`s). The board scrolls sideways on its own; the page never does. */
export function Board({ columns, gap, as = 'div', ...props }: BoardProps): ReactElement {
  return block(as, 'vello-board', { cols: columns === undefined ? undefined : String(columns), gap: gapAttr(gap) }, props);
}

export interface BoardColumnProps {
  /** The column title. */
  readonly title: ReactNode;
  /** The item count shown after the title (mono). */
  readonly count?: ReactNode;
  /** Unique within the page: names the column region. */
  readonly id: string;
  readonly children?: ReactNode;
}

/** One kanban column: a labelled region with a title, a count, and a stack of cards. */
export function BoardColumn({ id, title, count, children }: BoardColumnProps): ReactElement {
  const titleId = `${id}-title`;
  return (
    <section className="vello-board__col" aria-labelledby={titleId}>
      <h3 id={titleId} className="vello-board__head">
        <span>{title}</span>
        {count !== undefined && <span className="vello-board__count">{count}</span>}
      </h3>
      <div className="vello-board__body">{children}</div>
    </section>
  );
}

// ---- Spacer / Divider ----------------------------------------------------------------------------------------------------

/** Space step a fixed {@link Spacer} may take. */
export type SpacerSize = 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;

/** Pushes the rest of a `Row` to the end (no `size`), or leaves a fixed space of one step. Decorative: hidden from assistive tech. */
export function Spacer({ size }: { size?: SpacerSize }): ReactElement {
  return size === undefined ? <span className="vello-grow" aria-hidden="true" /> : <span className="vello-spacer" data-size={String(size)} aria-hidden="true" />;
}

export interface DividerProps {
  readonly orientation?: 'horizontal' | 'vertical';
  /** `subtle` (default) or the stronger `default` hairline. */
  readonly tone?: 'subtle' | 'default';
}

/** A hairline between blocks (an `<hr>`), or a vertical one between items of a `Row`. */
export function Divider({ orientation = 'horizontal', tone = 'subtle' }: DividerProps): ReactElement {
  if (orientation === 'vertical') return <div className="vello-divider" role="separator" aria-orientation="vertical" data-tone={tone === 'default' ? 'default' : undefined} />;
  return <hr className="vello-divider" data-tone={tone === 'default' ? 'default' : undefined} />;
}
